// Sprint F14 — Página pública do BM (Boletim de Medição).
// Layout reescrito espelhando o boletim impresso da Consplan (Excel):
// cabeçalho com dados contratuais, tabela de itens medidos, totalizadores
// do contrato, retenções fiscais e bloco de assinatura digital.
// Estética moderna, mobile-first, mantendo o azul institucional Hydrodata.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { fmtDataBr as _fmtDataBr } from '../utils/dataFmt';

const fmt = (v: any) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return 'R$ 0,00';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
};
const fmtData = _fmtDataBr;
const fmtDateTime = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleString('pt-BR') : '—';

interface BmDados {
  id: string;
  descricao: string;
  parcela_titulo: string | null;
  descricao_completa: string | null;
  valor_previsto: number;
  valor_liquido_previsto: number | null;
  valor_faturado: number | null;
  data_vencimento: string;
  tipo_parcela: string | null;
  bm_status: string | null;
  bm_assinado_em: string | null;
  bm_assinado_por_nome: string | null;
  bm_gerado_em: string | null;
  status: string;
  numero_parcela: number | null;
  total_parcelas: number | null;
  codigo_crea: string | null;
  prazo_dias: number | null;
  // Contrato
  contrato_id: string;
  contrato_numero: string;
  contrato_titulo: string;
  contrato_objeto: string | null;
  contrato_valor_base: number;
  contrato_valor_aditivos: number;
  contrato_valor_total: number;
  contrato_assinado_em: string | null;
  data_inicio_prevista: string | null;
  data_termino_previsto: string | null;
  // Cliente
  cliente_nome: string;
  cliente_cnpj: string | null;
  cliente_endereco: string | null;
  cliente_bairro: string | null;
  cliente_cep: string | null;
  // Auto-computado (backend)
  medicao_numero: number;
  bms_anteriores_qtd: number;
  valor_medido_anterior: number;
  valor_medido_acumulado_com_atual: number;
  saldo_contrato: number;
  retencoes_fiscais: number;
  valor_a_depositar: number;
  bm_anterior_data: string | null;
  entregaveis_vinculados: Array<{
    id?: string;
    descricao: string;
    data_entregue: string | null;
    status: string;
    quadro_servico_idx?: number | null;
  }>;
}

export default function PaginaPublicaBm({ hash }: { hash: string }) {
  const [bm, setBm] = useState<BmDados | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [nome, setNome] = useState('');
  const [aceito, setAceito] = useState(false);
  const [assinando, setAssinando] = useState(false);
  const [sucesso, setSucesso] = useState(false);

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [hash]);

  async function carregar() {
    setLoading(true);
    setErro('');
    try {
      const r = await axios.get(`${API_URL}/publico/proposta/${hash}`);
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (!d?.descricao || !d?.contrato_numero) {
        setErro('Link inválido ou expirado.');
      } else {
        setBm(d);
      }
    } catch (e: any) {
      setErro(e.response?.data?.error || e.response?.data?.message || 'Falha ao carregar BM.');
    } finally { setLoading(false); }
  }

  async function assinar() {
    setErro('');
    if (!nome.trim() || nome.trim().length < 4) {
      setErro('Digite seu nome completo (mínimo 4 letras).'); return;
    }
    if (!aceito) {
      setErro('Você precisa marcar o aceite pra prosseguir.'); return;
    }
    setAssinando(true);
    try {
      await axios.post(`${API_URL}/publico/proposta/${hash}`, {
        action: 'assinar_bm',
        nome: nome.trim(),
      });
      setSucesso(true);
      await carregar();
    } catch (e: any) {
      setErro(e.response?.data?.error || e.response?.data?.message || 'Falha ao assinar.');
    } finally { setAssinando(false); }
  }

  if (loading) {
    return <Wrapper><div style={{ padding: 60, textAlign: 'center', color: '#888' }}>Carregando…</div></Wrapper>;
  }
  if (erro && !bm) {
    return (
      <Wrapper>
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>⚠</div>
          <div style={{ color: '#B91626', fontSize: 14 }}>{erro}</div>
          <div style={{ color: '#888', fontSize: 12, marginTop: 12 }}>
            Solicite um novo link à Hydrodata Engenharia.
          </div>
        </div>
      </Wrapper>
    );
  }
  if (!bm) return null;

  const jaAssinado = !!bm.bm_assinado_em;
  const enderecoCompleto = [bm.cliente_endereco, bm.cliente_bairro, bm.cliente_cep]
    .filter(Boolean).join(' · ');
  // Período da medição: do BM anterior (ou início vigência) até hoje (ou geração do BM).
  const periodoIni = bm.bm_anterior_data
    ? new Date(new Date(bm.bm_anterior_data).getTime() + 24 * 3600 * 1000)
    : bm.data_inicio_prevista
      ? new Date(bm.data_inicio_prevista)
      : null;
  const periodoFim = bm.bm_gerado_em ? new Date(bm.bm_gerado_em) : new Date();
  const periodoStr = periodoIni
    ? `${periodoIni.toLocaleDateString('pt-BR')} a ${periodoFim.toLocaleDateString('pt-BR')}`
    : `até ${periodoFim.toLocaleDateString('pt-BR')}`;
  const mesAno = `${String(periodoFim.getMonth() + 1).padStart(2, '0')}/${periodoFim.getFullYear()}`;

  // Itens do BM: usa entregáveis se houver; senão, gera 1 linha única com a descrição da parcela.
  const itens = (bm.entregaveis_vinculados && bm.entregaveis_vinculados.length > 0)
    ? bm.entregaveis_vinculados.map((e, i) => ({
        num: i + 1,
        descricao: e.descricao,
        data_entregue: e.data_entregue,
        valor: bm.valor_previsto / bm.entregaveis_vinculados.length,
      }))
    : [{
        num: 1,
        descricao: bm.parcela_titulo || bm.descricao,
        data_entregue: null,
        valor: bm.valor_previsto,
      }];

  return (
    <Wrapper>
      {/* Header — Hydrodata + Medição Nº */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', borderBottom: '3px solid #020D7F',
        paddingBottom: 18, marginBottom: 22, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase',
            letterSpacing: 1.5, marginBottom: 4 }}>
            Hydrodata Engenharia
          </div>
          <h1 style={{ margin: 0, fontSize: 22, color: '#020D7F', fontWeight: 700 }}>
            📐 Boletim de Medição
          </h1>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase',
            letterSpacing: 1 }}>
            Medição Nº
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#020D7F', lineHeight: 1 }}>
            {bm.medicao_numero}
          </div>
          <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
            {mesAno}
          </div>
        </div>
      </div>

      {/* Dados contratuais — 2 colunas */}
      <SecaoBox titulo="Dados contratuais">
        <div style={{ display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          <Bloco rotulo="Empresa (cliente)">
            <div style={{ fontWeight: 600, color: '#222' }}>{bm.cliente_nome}</div>
            {bm.cliente_cnpj && (
              <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
                CNPJ: {bm.cliente_cnpj}
              </div>
            )}
            {enderecoCompleto && (
              <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
                {enderecoCompleto}
              </div>
            )}
          </Bloco>
          <Bloco rotulo="Contrato">
            <div style={{ fontWeight: 600, color: '#020D7F' }}>Nº {bm.contrato_numero}</div>
            {bm.contrato_titulo && (
              <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
                {bm.contrato_titulo}
              </div>
            )}
            {bm.codigo_crea && (
              <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
                CREA: <code style={{ fontSize: 10 }}>{bm.codigo_crea}</code>
              </div>
            )}
          </Bloco>
          {bm.contrato_objeto && (
            <Bloco rotulo="Objeto do contrato" full>
              <div style={{ fontSize: 12, color: '#333', lineHeight: 1.5 }}>
                {bm.contrato_objeto}
              </div>
            </Bloco>
          )}
          <Bloco rotulo="Período da medição">
            <div style={{ fontSize: 12, color: '#222' }}>{periodoStr}</div>
          </Bloco>
          <Bloco rotulo="Data de emissão">
            <div style={{ fontSize: 12, color: '#222' }}>
              {fmtData(periodoFim.toISOString().slice(0, 10))}
            </div>
            <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
              Vencimento da parcela: {fmtData(bm.data_vencimento)}
            </div>
          </Bloco>
        </div>
      </SecaoBox>

      {/* Controle de medições — tabela de itens */}
      <SecaoBox titulo="Controle de medições">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#020D7F', color: '#fff' }}>
                <th style={th}>Item</th>
                <th style={{ ...th, textAlign: 'left' }}>Descrição</th>
                <th style={th}>Unid.</th>
                <th style={th}>Quant.</th>
                <th style={{ ...th, textAlign: 'right' }}>Valor medido</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((it, idx) => (
                <tr key={idx} style={{
                  borderBottom: '0.5px solid #e0e0e0',
                  background: idx % 2 ? '#FAFBFF' : '#fff',
                }}>
                  <td style={td}>{it.num}</td>
                  <td style={{ ...td, textAlign: 'left' }}>
                    <div style={{ fontWeight: 500, color: '#222' }}>{it.descricao}</div>
                    {it.data_entregue && (
                      <div style={{ fontSize: 10, color: '#0a7f4a', marginTop: 2 }}>
                        ✓ entregue em {fmtData(it.data_entregue)}
                      </div>
                    )}
                  </td>
                  <td style={td}>UNID.</td>
                  <td style={td}>1</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: '#020D7F' }}>
                    {fmt(it.valor)}
                  </td>
                </tr>
              ))}
              <tr style={{ background: '#EFF3FF', fontWeight: 600 }}>
                <td colSpan={4} style={{ ...td, textAlign: 'right', color: '#020D7F' }}>
                  Total medido neste BM
                </td>
                <td style={{ ...td, textAlign: 'right', fontSize: 14, color: '#020D7F' }}>
                  {fmt(bm.valor_previsto)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {bm.tipo_parcela === 'mobilizacao' && itens.length === 1 && !itens[0].data_entregue && (
          <div style={{ fontSize: 11, color: '#7C3AED', fontStyle: 'italic',
            background: '#F3E8FF', padding: 10, borderRadius: 4, marginTop: 10 }}>
            🚀 Parcela de <strong>mobilização</strong> — liberada pela assinatura do contrato,
            não requer entrega prévia.
          </div>
        )}
      </SecaoBox>

      {/* Totalizadores — saldos contratuais */}
      <SecaoBox titulo="Saldos contratuais">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0,
          border: '0.5px solid #e0e0e0', borderRadius: 6, overflow: 'hidden' }}>
          <LinhaTotal
            rotulo="Valor total do contrato"
            valor={bm.contrato_valor_base}
            cor="#020D7F" />
          {bm.contrato_valor_aditivos > 0 && (
            <LinhaTotal
              rotulo="(+) Aditivos"
              valor={bm.contrato_valor_aditivos}
              cor="#7C3AED" borda />
          )}
          {bm.contrato_valor_aditivos > 0 && (
            <LinhaTotal
              rotulo="Valor total (com aditivos)"
              valor={bm.contrato_valor_total}
              cor="#020D7F" topo />
          )}
          <LinhaTotal
            rotulo="(−) Medido em BMs anteriores"
            valor={bm.valor_medido_anterior}
            cor="#0a7f4a" borda
            sub={bm.bms_anteriores_qtd > 0
              ? `${bm.bms_anteriores_qtd} BM${bm.bms_anteriores_qtd > 1 ? 's' : ''} aprovado${bm.bms_anteriores_qtd > 1 ? 's' : ''}`
              : 'Primeiro BM deste contrato'} />
          <LinhaTotal
            rotulo="(−) Medido neste BM"
            valor={bm.valor_previsto}
            cor="#B07500" borda destaque />
          <LinhaTotal
            rotulo="Saldo do contrato após este BM"
            valor={bm.saldo_contrato}
            cor={bm.saldo_contrato > 0 ? '#020D7F' : '#0a7f4a'}
            topo destaque />
        </div>
      </SecaoBox>

      {/* Retenções fiscais — só mostra se houver */}
      {bm.retencoes_fiscais > 0 && (
        <SecaoBox titulo="Retenções fiscais na fonte">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0,
            border: '0.5px solid #e0e0e0', borderRadius: 6, overflow: 'hidden' }}>
            <LinhaTotal rotulo="Valor bruto" valor={bm.valor_previsto} cor="#666" />
            <LinhaTotal
              rotulo="(−) IRPJ + PIS + COFINS + CSLL"
              valor={bm.retencoes_fiscais}
              cor="#B91626" borda />
            <LinhaTotal
              rotulo="Líquido a depositar"
              valor={bm.valor_a_depositar}
              cor="#0a7f4a" topo destaque />
          </div>
        </SecaoBox>
      )}

      {/* Assinatura — mantida do modelo anterior */}
      <div style={{ marginTop: 28, padding: 20,
        background: jaAssinado ? '#E8F5EE' : '#FFF8E8',
        borderRadius: 8, border: `1px solid ${jaAssinado ? '#0a7f4a' : '#FFE9C2'}` }}>
        {jaAssinado ? (
          <>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#0a7f4a', marginBottom: 6 }}>
              ✓ BM aprovado pelo cliente
            </div>
            <div style={{ fontSize: 12, color: '#555' }}>
              Assinado por <strong>{bm.bm_assinado_por_nome}</strong> em{' '}
              <strong>{fmtDateTime(bm.bm_assinado_em)}</strong>
            </div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
              A Hydrodata Engenharia foi notificada e prosseguirá com a emissão da nota fiscal.
            </div>
          </>
        ) : sucesso ? (
          <div style={{ textAlign: 'center', padding: 10 }}>
            <div style={{ fontSize: 32 }}>✅</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#0a7f4a', marginTop: 6 }}>
              BM assinado com sucesso!
            </div>
            <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>
              Carregando confirmação…
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#B07500', marginBottom: 12 }}>
              📝 Aprovação eletrônica
            </div>
            <div style={{ fontSize: 12, color: '#555', marginBottom: 16, lineHeight: 1.5 }}>
              Confira os dados acima. Ao assinar, você confirma que os serviços foram entregues
              conforme descrito e autoriza a emissão da nota fiscal correspondente.
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: '#555', fontWeight: 600,
                display: 'block', marginBottom: 4 }}>
                Seu nome completo *
              </label>
              <input value={nome} onChange={e => setNome(e.target.value)}
                placeholder="Nome completo (igual no documento de identidade)"
                style={{ width: '100%', padding: '10px 12px', fontSize: 13, borderRadius: 5,
                  border: '0.5px solid #ccc', boxSizing: 'border-box' }} />
            </div>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 11,
              color: '#555', cursor: 'pointer', marginBottom: 14, lineHeight: 1.5 }}>
              <input type="checkbox" checked={aceito}
                onChange={e => setAceito(e.target.checked)}
                style={{ marginTop: 2 }} />
              <span>
                Confirmo que sou o representante autorizado de <strong>{bm.cliente_nome}</strong>,
                que recebi e validei a entrega descrita acima, e que esta assinatura
                eletrônica tem validade jurídica equivalente à assinatura manuscrita
                conforme MP 2.200-2/2001.
              </span>
            </label>
            {erro && (
              <div style={{ padding: 10, background: '#FFF7F7', border: '0.5px solid #FBD9D9',
                borderRadius: 5, color: '#B91626', fontSize: 12, marginBottom: 12 }}>
                ⚠ {erro}
              </div>
            )}
            <button onClick={assinar} disabled={assinando || !nome.trim() || !aceito}
              style={{ width: '100%', padding: '14px 20px', fontSize: 14, fontWeight: 600,
                background: (nome.trim() && aceito) ? '#0a7f4a' : '#bbb', color: '#fff',
                border: 'none', borderRadius: 6,
                cursor: assinando ? 'wait' : ((nome.trim() && aceito) ? 'pointer' : 'not-allowed') }}>
              {assinando ? 'Assinando…' : '✓ Aprovar e assinar BM'}
            </button>
          </>
        )}
      </div>

      <div style={{ fontSize: 10, color: '#888', textAlign: 'center', marginTop: 28,
        paddingTop: 14, borderTop: '0.5px solid #eee' }}>
        Hydrodata Engenharia · Sistema de Boletins de Medição · Link único e seguro
      </div>
    </Wrapper>
  );
}

// ─── Helpers de layout ──────────────────────────────────────────────────────

function Wrapper({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: '#F0F1F5',
      padding: '40px 16px', display: 'flex', justifyContent: 'center' }}>
      <div style={{ background: '#fff', padding: 32, borderRadius: 8,
        maxWidth: 800, width: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {children}
      </div>
    </div>
  );
}

function SecaoBox({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#020D7F',
        textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 8 }}>
        {titulo}
      </div>
      {children}
    </div>
  );
}

function Bloco({ rotulo, full, children }: {
  rotulo: string; full?: boolean; children: React.ReactNode;
}) {
  return (
    <div style={{
      padding: 12, background: '#FAFBFF',
      border: '0.5px solid #E4E7F1', borderRadius: 6,
      gridColumn: full ? '1 / -1' : undefined,
    }}>
      <div style={{ fontSize: 9, color: '#888', textTransform: 'uppercase',
        letterSpacing: 0.8, fontWeight: 600, marginBottom: 4 }}>
        {rotulo}
      </div>
      {children}
    </div>
  );
}

function LinhaTotal({ rotulo, valor, cor, borda, topo, destaque, sub }: {
  rotulo: string; valor: number; cor: string;
  borda?: boolean; topo?: boolean; destaque?: boolean; sub?: string;
}) {
  return (
    <>
      <div style={{
        padding: '10px 14px',
        borderTop: topo ? '0.5px solid #e0e0e0' : 'none',
        borderLeft: borda ? '0.5px solid #e0e0e0' : 'none',
        background: destaque ? '#FAFBFF' : '#fff',
        fontSize: 12, color: '#666',
      }}>
        {rotulo}
        {sub && <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>{sub}</div>}
      </div>
      <div style={{
        padding: '10px 14px',
        borderTop: topo ? '0.5px solid #e0e0e0' : 'none',
        background: destaque ? '#FAFBFF' : '#fff',
        textAlign: 'right',
        fontSize: destaque ? 16 : 13,
        fontWeight: destaque ? 700 : 600,
        color: cor,
      }}>
        {fmt(valor)}
      </div>
    </>
  );
}

const th: React.CSSProperties = {
  padding: '10px 8px', textAlign: 'center', fontSize: 11,
  fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase',
};
const td: React.CSSProperties = {
  padding: '10px 8px', textAlign: 'center', verticalAlign: 'middle',
};
